/**
 * Last-resort error boundary.
 *
 * React must not unmount the whole application because one screen threw: this boundary keeps the shell's failure
 * contained to a readable message. It is a class component because there is no hook equivalent of
 * `componentDidCatch`, and it uses `i18n.t` directly because hooks are not available in a class.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import i18n from '@/lib/i18n';

/** Props accepted by {@link ErrorBoundary}. */
export interface ErrorBoundaryProps {
  /** Subtree to guard. */
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/** Renders a recoverable fallback when a descendant throws during rendering. */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = { hasError: false };

  /** Switches to the fallback. */
  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  /** Logs the failure; in production this is where a telemetry sink would be called. */
  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console -- a render crash must leave a trace even without telemetry wired up.
    console.error('Unhandled render error', error, info.componentStack);
  }

  /** Renders the fallback with a reload action. */
  public override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="card-surface w-full max-w-md p-6 text-center">
          <h1 className="text-lg font-semibold">{i18n.t('serverError.title')}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{i18n.t('serverError.body')}</p>
          <Button className="mt-6 w-full" onClick={() => window.location.reload()}>
            {i18n.t('serverError.reload')}
          </Button>
        </div>
      </div>
    );
  }
}
